/**
 * Helpers for dealing with the `payload` of a pre-lead.
 *
 * The backend stores whatever the listing portal sent, verbatim - the shape is
 * NOT fixed and differs between 99acres and MagicBricks, and either portal can
 * change theirs without telling us. Everything here is therefore written to
 * degrade to a sensible fallback rather than assume a key exists.
 *
 * Keep the guessing logic in this file so it is cheap to fix once we have seen
 * what real payloads actually look like.
 */

import moment from "moment";

const MAX_DEPTH = 6;

// Keys are compared with punctuation and case stripped, so a single candidate
// like "fullname" also matches "Full_Name", "fullName" and "FULL NAME".
const NAME_KEYS = [
  "name",
  "fullname",
  "customername",
  "clientname",
  "contactname",
  "leadname",
  "sendername",
  "username",
  "firstname",
];

const PHONE_KEYS = [
  "phone",
  "phoneno",
  "phonenumber",
  "mobile",
  "mobileno",
  "mobilenumber",
  "contactno",
  "contactnumber",
  "telephone",
  "cell",
];

const EMAIL_KEYS = ["email", "emailid", "emailaddress", "mail"];

const PROJECT_KEYS = [
  "projectname",
  "project",
  "propertyname",
  "property",
  "buildingname",
  "societyname",
];

const normalizeKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

const isPlainValue = (value) =>
  value !== null && value !== undefined && typeof value !== "object";

const isEmptyValue = (value) =>
  value === null || value === undefined || String(value).trim() === "";

/**
 * Depth-first search for the first non-empty primitive whose key matches one of
 * `candidateKeys`. Matches the whole normalized key first across the entire
 * payload, then falls back to a substring match, so `{ mobile: ... }` wins over
 * `{ alternate_mobile_number: ... }` regardless of key ordering.
 */
const findByKeys = (payload, candidateKeys) => {
  const exact = searchPayload(payload, candidateKeys, 0, true);
  if (exact !== undefined) return exact;
  return searchPayload(payload, candidateKeys, 0, false);
};

const searchPayload = (node, candidateKeys, depth, exactOnly) => {
  if (!node || typeof node !== "object" || depth > MAX_DEPTH) return undefined;

  const entries = Array.isArray(node)
    ? node.map((value, index) => [String(index), value])
    : Object.entries(node);

  // Check this level before descending, so shallow keys beat deep ones.
  for (const [key, value] of entries) {
    if (!isPlainValue(value) || isEmptyValue(value)) continue;
    const normalized = normalizeKey(key);
    const matched = exactOnly
      ? candidateKeys.includes(normalized)
      : candidateKeys.some((candidate) => normalized.includes(candidate));
    if (matched) return value;
  }

  for (const [, value] of entries) {
    const found = searchPayload(value, candidateKeys, depth + 1, exactOnly);
    if (found !== undefined) return found;
  }

  return undefined;
};

export const getPayloadName = (payload) => findByKeys(payload, NAME_KEYS);
export const getPayloadPhone = (payload) => findByKeys(payload, PHONE_KEYS);
export const getPayloadEmail = (payload) => findByKeys(payload, EMAIL_KEYS);
export const getPayloadProject = (payload) => findByKeys(payload, PROJECT_KEYS);

/**
 * Best-effort label for a pre-lead in the list view. Falls back through the
 * portal's own id to our id, so this never renders blank.
 */
export const getPreLeadDisplayName = (preLead) => {
  if (!preLead) return "-";
  const name = getPayloadName(preLead.payload);
  if (!isEmptyValue(name)) return String(name);
  if (!isEmptyValue(preLead.external_id)) return String(preLead.external_id);
  return `#${preLead.id}`;
};

/**
 * Flattens an arbitrarily shaped payload into `{ path, value }` rows for a
 * generic key/value table. Objects and arrays are walked; anything past
 * MAX_DEPTH, and any value we cannot flatten, is stringified instead of dropped.
 */
export const flattenPayload = (payload, parentPath = "", depth = 0) => {
  if (payload === null || payload === undefined) return [];

  if (typeof payload !== "object") {
    return [{ path: parentPath || "value", value: payload }];
  }

  const entries = Array.isArray(payload)
    ? payload.map((value, index) => [`[${index}]`, value])
    : Object.entries(payload);

  if (entries.length === 0) {
    return [
      {
        path: parentPath || "payload",
        value: Array.isArray(payload) ? "[]" : "{}",
      },
    ];
  }

  return entries.flatMap(([key, value]) => {
    const path = parentPath
      ? `${key.startsWith("[") ? `${parentPath}${key}` : `${parentPath}.${key}`}`
      : String(key);

    if (value !== null && typeof value === "object") {
      if (depth >= MAX_DEPTH) {
        return [{ path, value: safeStringify(value) }];
      }
      return flattenPayload(value, path, depth + 1);
    }

    return [{ path, value }];
  });
};

export const safeStringify = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** Renders any leaf value as a string without turning null/false into "". */
export const formatPayloadValue = (value) => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (String(value).trim() === "") return "-";
  return String(value);
};

export const PRE_LEAD_SOURCES = [
  { value: "99acres", label: "99acres" },
  { value: "magicbricks", label: "MagicBricks" },
];

// `processed` and `failed` are reserved for the future conversion step and are
// never set today. They render if they appear, but no UI is built around them.
export const PRE_LEAD_STATUSES = [
  { value: "pending", label: "Pending", color: "blue" },
  { value: "dismissed", label: "Dismissed", color: "default" },
  { value: "positive", label: "Positive", color: "green" },
  { value: "processed", label: "Processed", color: "green" },
  { value: "failed", label: "Failed", color: "red" },
];

// Buckets are computed server-side in IST and are mutually exclusive. Never
// recompute these client-side from the raw UTC value - outside IST, and around
// midnight, the browser would disagree with the server.
export const FOLLOW_UP_FILTERS = [
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "upcoming", label: "Upcoming" },
  { value: "none", label: "No follow-up" },
];

export const getStatusColor = (status) =>
  PRE_LEAD_STATUSES.find((s) => s.value === status)?.color || "default";

export const getStatusLabel = (status) =>
  PRE_LEAD_STATUSES.find((s) => s.value === status)?.label || status || "-";

export const getSourceLabel = (source) =>
  PRE_LEAD_SOURCES.find((s) => s.value === source)?.label || source || "-";

// The API has no config.time_zone set, so every timestamp it returns is UTC.
// Business hours are IST, so display is pinned to +05:30 rather than left to
// the browser's local zone.
const IST_OFFSET_MINUTES = 330;

export const formatIst = (value, format = "DD/MM/YYYY hh:mm A") => {
  if (!value) return "-";
  // Parsed as strict ISO: the API only ever sends ISO8601, and this keeps a
  // malformed value from falling back to `new Date()`, which logs a noisy
  // deprecation trace on every render.
  const m = moment(value, moment.ISO_8601).utcOffset(IST_OFFSET_MINUTES);
  return m.isValid() ? m.format(format) : "-";
};

export const formatIstDate = (value) => formatIst(value, "DD/MM/YYYY");

/**
 * Builds a display name from the compact user object the pre-lead serializer
 * returns. Either name part can independently be null, so this falls back to
 * email and then to the id.
 */
export const getUserDisplayName = (user) => {
  if (!user) return "-";
  const name = [user.first_name, user.last_name]
    .filter((part) => part && String(part).trim() !== "")
    .join(" ");
  if (name) return name;
  return user.email || `#${user.id}`;
};
